import $ from "jquery";
import Marionette from "backbone.marionette";
import AnimatedRegion from "marionette-animated-region";

import { MATCH_SCORE_THRESHOLD } from "lib/config";
import { SUGGESTION_SOURCE } from "lib/constants";
import {
    calculateMatchScore,
    getSavedPositions,
    addSavedPosition,
    removeSavedPosition,
} from "lib/util";
import wsStorage from "lib/wsStorage";

import component from "../components";
import SuggestionView, { loadSuggestions } from "./suggestion";
import events from "../events";

import emptyTemplate from "../templates/candidate/matches-empty.jst";

function _updateMatchesCount(count) {
    const $matchesCount = $(".js-matches-count");
    if (count === 0) {
        $matchesCount.remove();
    } else {
        $matchesCount.text(count).show().addClass("nav__badge--visible");
    }
}

function _updateSavedCount(count) {
    const $savedCount = $(".js-saved-count");
    if (count === 0) {
        $savedCount.remove();
    } else {
        $savedCount.text(count);
    }
}

export function updateMatchesCount() {
    const $matchesCount = $(".js-matches-count");
    if (!$matchesCount.length) {
        return;
    }

    const answerCount = window.answerCount || 0;
    if (calculateMatchScore(answerCount) < MATCH_SCORE_THRESHOLD) {
        _updateMatchesCount(1);
        return;
    }

    loadSuggestions({ matches: 1 }).done(({ positions }) => {
        const count = positions.length;
        _updateMatchesCount(count);

        if (count) {
            events.track("MATCHES_COUNT", count);
        }
    });
}

const SavedView = Marionette.View.extend({
    template: emptyTemplate,
    serializeData() {
        return {
            ...this.options,
            answerCount: window.answerCount || 0,
            unansweredCount: window.unansweredCount || 0,
        };
    },
});

export default Marionette.View.extend({
    template: false,
    regions: {
        viewContainer: {
            selector: ".animated-region",
            regionClass: AnimatedRegion,
            currentView: true,
            animation: {
                showAnimation: [
                    {
                        properties: "transition.slideRightBigIn",
                    },
                ],
                hideAnimation: [
                    {
                        properties: "transition.slideLeftBigOut",
                    },
                ],
            },
        },
    },
    ui: {
        popup: ".matches__popup",
    },
    events: {
        "click @ui.popup": "onHidePopup",
    },
    initialize() {
        const count = window.answerCount || 0;
        this.canShow = calculateMatchScore(count) >= MATCH_SCORE_THRESHOLD;
    },
    onRender() {
        if (!this.canShow) {
            this.showFinish();
            return;
        }

        this.showChildView(
            "viewContainer",
            new component.LoadingComponent({
                title: "Loading your matches",
            }),
        );

        loadSuggestions({ matches: 1 }).done(({ group, positions }) => {
            this.group = group;
            this.positions = positions;
            this.count = positions.length;

            _updateMatchesCount(this.count);

            if (this.count === 0) {
                this.showFinish();
                return;
            }

            const suggest = new SuggestionView({
                positions,
                group,
                source: SUGGESTION_SOURCE.MATCHES,
                showNav: true,
                maxApproved: this.count,
                utm_medium: "matches-apply",
            });

            this.listenTo(suggest, "suggest:empty", this.showEmptyView);
            this.listenTo(suggest, "suggest:approve", this.onApprove);
            this.listenTo(suggest, "suggest:pass", this.onPass);
            this.listenTo(suggest, "suggest:undo", this.onUndo);
            this.listenTo(suggest, "suggest:finish", this.showFinish);

            this.showChildView("viewContainer", suggest);

            // Wait for the fade in of the container a bit
            setTimeout(() => {
                if (!wsStorage.getItem("matches-pop")) {
                    this.ui.popup.fadeIn("slow");
                }
            }, 200);
        });
    },
    hidePopup() {
        if (this.ui.popup.is(":visible")) {
            this.ui.popup.fadeOut("fast");
            wsStorage.setItem("matches-pop", 1);
        }
    },
    showEmptyView() {
        const saved = getSavedPositions();
        this.showChildView("viewContainer", new SavedView({ saved }));
    },
    onHidePopup(event) {
        event.preventDefault();

        this.hidePopup();
    },
    onApprove(position) {
        this.saveMatch();

        this.updateSaved(addSavedPosition(position));
    },
    onPass() {
        this.saveMatch();
    },
    onUndo(position) {
        this.count += 1;
        _updateMatchesCount(this.count);

        if (!position.accepted) {
            return;
        }

        this.updateSaved(removeSavedPosition(position.id));
    },
    saveMatch() {
        this.hidePopup();

        this.count = Math.max(0, this.count - 1);
        _updateMatchesCount(this.count);
    },
    updateSaved(saved) {
        _updateSavedCount(saved.length);
    },
    showFinish() {
        this.showChildView(
            "viewContainer",
            new SavedView({
                saved: getSavedPositions(),
                canShow: this.canShow,
            }),
        );
    },
});
